import { NavigationSettingAri } from '@atlassian/ari/navigation';

const MENU_DATA_KEY = 'nav-plat-l1-menu-data' as const;
const SIDE_BAR_DATA_KEY = 'nav-plat-sidebar-data' as const;

/**
 * The inputs used to derive the ARI of a navigation-customization record.
 *
 * These are the same inputs carried by `NetworkStorageParams` (plus the
 * first-party app identifier, which the navigation components already receive
 * as a separate prop).
 *
 * @see NetworkStorageParams in `@atlassian/navigation-common/types`
 */
export interface BuildNavigationAriParams {
	/**
	 * The id of the app-instance within a site.
	 */
	activationId: string;
	/**
	 * The first-party app identifier, e.g. `confluence`, `jira`, `home`.
	 */
	firstPartyApp: string;
	/**
	 * The id of the site (which may contain many activated-products).
	 */
	siteId: string;
}

interface BuildNavigationAriParamsInternal extends BuildNavigationAriParams {
	settingId: string;
}

/**
 * Derives the ARI of the record holding the L1 menu customization.
 *
 * Every caller — query variables and mutation input alike — must derive the ARI
 * through this function so that reads and writes cannot target different
 * records.
 */
export function buildNavigationAriForMenuData(params: BuildNavigationAriParams): string {
	return buildNavigationAri({ ...params, settingId: MENU_DATA_KEY });
}

/**
 * Derives the ARI of the record holding the side nav customization (including
 * the side nav's collapsed state, which both GlobalTopNavigation and
 * GlobalSideNavigation read and write).
 *
 * Every caller — query variables and mutation input alike — must derive the ARI
 * through this function so that reads and writes cannot target different
 * records.
 */
export function buildNavigationAriForSidebarData(params: BuildNavigationAriParams): string {
	return buildNavigationAri({ ...params, settingId: SIDE_BAR_DATA_KEY });
}

function buildNavigationAri(params: BuildNavigationAriParamsInternal) {
	return homeOverride(NavigationSettingAri.create(adjustParams(params)).toString());
}

// For the time being (Sept 2025), Home will be the only townsquare app that stores network settings.
// The other apps (Goals, Teams &Projects) are in the process of being individually commissioned
// (meaning they will have their own siteId and/or activationId). In the meantime, they will NOT
// be using the settings service, so we don't need an override for them.
function adjustParams(params: BuildNavigationAriParamsInternal) {
	if (params.firstPartyApp === 'home') {
		return {
			...params,
			settingId: `${params.settingId}-${params.firstPartyApp}`,
		};
	}
	return params;
}

function homeOverride(ari: string) {
	return ari.replace('/home/', '/townsquare/');
}
